import {
  useAeropayUser,
  useCheckoutRequirements,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { CardCarousel } from '@iheartjane/menu';
import { Flex } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import { useState } from 'react';

import { MenuQuickCheckoutDrawer } from '@jane/shared-ecomm/components';
import type { QuickCart } from '@jane/shared-ecomm/data-access';
import {
  useCreateQuickCart,
  useFetchStoreProduct,
  useGetQuickCarts,
} from '@jane/shared-ecomm/data-access';
import { useMenu } from '@jane/shared-ecomm/providers';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct } from '@jane/shared/models';
import { monolithPaths } from '@jane/shared/util';

import { PreviousOrderCard } from './PreviousOrderCard';

export const MenuQuickCheckoutRow = () => {
  const expirationFieldEnabled = useFlag(FLAGS.c3MarketplaceMedIdExpiration);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    appInfo: { appMode },
    dispatches: { attemptToAddToCart },
    store,
  } = useMenu();
  const { id, jane_pay_integration } = store;
  const jwt = getJwt();
  const { data: janeUser } = useJaneUser();
  const { data: aeropayUser } = useAeropayUser({
    janeUserId: janeUser?.user?.id || null,
    jwt,
  });

  const enableQuickCarts =
    !!aeropayUser?.bank_accounts &&
    aeropayUser?.bank_accounts.length > 0 &&
    !!jane_pay_integration?.enabled;
  const { data: quickCarts, isPending } = useGetQuickCarts(
    { storeId: Number(id), limit: 10 },
    {
      enabled: enableQuickCarts,
    }
  );
  const {
    mutate: createCart,
    isSuccess,
    data: createdCart,
  } = useCreateQuickCart();
  const { mutateAsync: fetchStoreProduct } = useFetchStoreProduct();

  //For MVP purposes, we will exclude stores with required checkout docs from quick checkout
  const {
    data: checkoutRequirementsData,
    isPending: isCheckoutRequirementsLoading,
  } = useCheckoutRequirements(
    {
      storeId: Number(store.id) || null,
      jwt,
    },
    // Quick checkout is signed-in only, so keep skipping the fetch without a jwt.
    { enabled: !!jwt }
  );
  const hasRequiredCheckoutDocs =
    store.delivery_id_required ||
    store.pickup_id_required ||
    ['number', 'photo', 'photo_and_number'].includes(
      checkoutRequirementsData?.medical_documentation_requirement || ''
    ) ||
    (expirationFieldEnabled &&
      !!checkoutRequirementsData?.medical_expiration_date_required);

  if (
    !enableQuickCarts ||
    !quickCarts?.quick_carts ||
    quickCarts.quick_carts.length === 0 ||
    (!isCheckoutRequirementsLoading && hasRequiredCheckoutDocs)
  )
    return null;

  const handleAddToCartClick = async ({
    productId,
    priceId,
    count,
  }: {
    count: number;
    priceId: PriceId;
    productId: number;
  }) => {
    const menuProduct = await fetchStoreProduct({
      productId,
      storeId: Number(id),
    });
    if (menuProduct) {
      attemptToAddToCart({
        store,
        menuProduct: {
          ...menuProduct,
          id: productId,
        } as unknown as MenuProduct,
        price_id: priceId,
        count,
        location: 'menu',
      });
    }
  };

  const handleBuyAgainClick = (quickCart: QuickCart) => {
    const availableProducts = quickCart.products.filter(
      (product) => product.is_available
    );
    createCart(
      {
        app_mode: appMode,
        products: availableProducts,
        reservation_mode: quickCarts.checkout_preferences.reservation_mode,
        store_id: Number(id),
      },
      {
        onSuccess: () => {
          setIsDrawerOpen(true);
        },
      }
    );
  };

  return (
    <Flex flexDirection="column" gap={24} id="previous-orders-row">
      <CardCarousel
        name="Your previous orders"
        mode="ideal-width"
        cardGap={24}
        cardGapMobile={16}
        cardHeight={464}
        cardHeightMobile={484}
        cardWidth={420}
        cardWidthMobile={275}
        isLoading={isPending}
      >
        {quickCarts.quick_carts.map((quickCart, i) => (
          <CardCarousel.Card
            width="100%"
            widthMobile="100%"
            key={`${i}-${quickCart.date}`}
          >
            <PreviousOrderCard
              onAddToCartClick={handleAddToCartClick}
              onBuyAgainClick={() => handleBuyAgainClick(quickCart)}
              reservationPath={monolithPaths.reservation(
                quickCart.reservation_id
              )}
              quickCart={{ ...quickCart, id: i + 1 }}
            />
          </CardCarousel.Card>
        ))}
      </CardCarousel>
      {isSuccess && (
        <MenuQuickCheckoutDrawer
          checkoutPreferences={quickCarts.checkout_preferences}
          createdCart={createdCart.cart}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      )}
    </Flex>
  );
};
